import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FateControllerService, FateLinkDropdownComponent, FateType } from 'fate-editor';
import { AccentedCharDropdownComponent } from './dropdowns/accented-char-dropdown/accented-char-dropdown.component';
import { BibleDropdownComponent } from './dropdowns/bible-dropdown/bible-dropdown.component';
import { FontColorComponent } from './dropdowns/font-color/font-color.component';
import { FontHighlightComponent } from './dropdowns/font-highlight/font-highlight.component';
import { FontSizeComponent } from './dropdowns/font-size/font-size.component';
import { FontStyleComponent } from './dropdowns/font-style/font-style.component';
import { FontComponent } from './dropdowns/font/font.component';
import { LegalCodeDropdownComponent } from './dropdowns/legal-code-dropdown/legal-code-dropdown.component';
import { MishnaDropdownComponent } from './dropdowns/mishna-dropdown/mishna-dropdown.component';
import { NoteDropdownComponent } from './dropdowns/note-dropdown/note-dropdown.component';
import { SpecialCharDropdownComponent } from './dropdowns/special-char-dropdown/special-char-dropdown.component';
import { TalmudBavliDropdownComponent } from './dropdowns/talmud-bavli-dropdown/talmud-bavli-dropdown.component';
import { TalmudYerushalmiDropdownComponent } from './dropdowns/talmud-yerushalmi-dropdown/talmud-yerushalmi-dropdown.component';
import { ToseftaDropdownComponent } from './dropdowns/tosefta-dropdown/tosefta-dropdown.component';

/**Editor customizzato basato su FATE editor */
@Component({
  selector: 'app-custom-editor',
  templateUrl: './custom-editor.component.html',
  styleUrls: ['./custom-editor.component.css']
})
export class CustomEditorComponent {
  /**Identificativo univoco di un editor, per gestire la presenza di più editor attivi */
  @Input() editorId: any;
  /**Contenuto dell'editor in fase iniziale */
  @Input() content: string;
  /**Tipo di editor da implementare, per accedere alle opportune icone */
  @Input() tipoEditor: string;
  /**Evento su cui viene emessa la stringa in salvataggio */
  @Output() saving = new EventEmitter<string>();
  /**Evento su cui viene emessa la chiusura dell'editor */
  @Output() closing = new EventEmitter();
  /**Elenco delle icone disponibili per i diversi tipi di editor */
  elencoIcone = { //versione senza undo redo view-code
    'descrizione': ['c-bold', 'c-italic', 'c-underline', 'smallcaps', 'separator', 'clean', 'separator', 'special-char', 'accented-char', 'bible', 'legal-code', 'mishna', 'talmud-bavli', 'talmud-yerushalmi', 'tosefta'],
    'traduzione': ['c-bold', 'c-italic', 'c-underline', 'smallcaps', 'separator', 'note', 'glos-concept', 'glos-linguistic', 'glos-idiom', 'glos-rabbi', 'glos-nature', 'glos-name', 'glos-daily', 'separator', 'clean', 'separator', 'special-char', 'accented-char', 'bible', 'legal-code', 'mishna', 'talmud-bavli', 'talmud-yerushalmi', 'tosefta'],
    'note-personali': [
      'c-bold', 'c-italic', 'c-underline', 'strike', 'subscript', 'superscript', 'separator',
      'font', 'font-size', 'font-style', 'separator',
      'font-color', 'font-highlight', 'clean', 'separator',
      'ordered', 'unordered', 'separator',
      'indent', 'outdent', 'separator',
      'center', 'justify', 'left', 'right', 'separator',
      'undo', 'redo', 'separator',
      'horizontal-rule', 'image', 'link'
    ],
    'titoli': ['undo', 'redo', 'clean', 'separator', 'special-char', 'accented-char'],
    'titoli-corsivi': ['c-italic', 'separator', 'undo', 'redo', 'clean', 'separator', 'special-char', 'accented-char']
  };

  /**Contenuto della finestra di visualizzazione codice */
  contentCode: string = '';
  /**Se viene visualizzata la finestra codice o quella di editor normale */
  codeView: boolean = false;

  /**
   * Costruttore per il componente CustomEditorComponent
   * @param fateControllerService {FateControllerService} metodi comuni ereditati da Fate editor
   */
  constructor(
    private fateControllerService: FateControllerService
  ) {
    if (!fateControllerService.getAction('note')) { //controllo per non inizializzare più volte un'azione, causerebbe errore in presenza di più editor
      fateControllerService.registerAction(
        'note',
        {
          command: 'insertHTML',
          name: 'note',
          label: 'N',
          detect: ('note' as FateType),
          dropdown: NoteDropdownComponent
        }
      );
    }
    if (!fateControllerService.getAction('special-char')) { //controllo per non inizializzare più volte un'azione, causerebbe errore in presenza di più editor
      fateControllerService.registerAction(
        'special-char',
        {
          command: 'insertHTML',
          name: 'special-char',
          dropdown: SpecialCharDropdownComponent
        }
      );
    }
    if (!fateControllerService.getAction('accented-char')) { //controllo per non inizializzare più volte un'azione, causerebbe errore in presenza di più editor
      fateControllerService.registerAction(
        'accented-char',
        {
          command: 'insertHTML',
          name: 'accented-char',
          dropdown: AccentedCharDropdownComponent
        }
      );
    }
    if (!fateControllerService.getAction('bible')) { //controllo per non inizializzare più volte un'azione, causerebbe errore in presenza di più editor
      fateControllerService.registerAction(
        'bible',
        {
          command: 'insertHTML',
          name: 'bible',
          dropdown: BibleDropdownComponent
        }
      );
    }
    if (!fateControllerService.getAction('legal-code')) { //controllo per non inizializzare più volte un'azione, causerebbe errore in presenza di più editor
      fateControllerService.registerAction(
        'legal-code',
        {
          command: 'insertHTML',
          name: 'legal-code',
          dropdown: LegalCodeDropdownComponent
        }
      );
    }
    if (!fateControllerService.getAction('mishna')) { //controllo per non inizializzare più volte un'azione, causerebbe errore in presenza di più editor
      fateControllerService.registerAction(
        'mishna',
        {
          command: 'insertHTML',
          name: 'mishna',
          dropdown: MishnaDropdownComponent
        }
      );
    }
    if (!fateControllerService.getAction('talmud-bavli')) { //controllo per non inizializzare più volte un'azione, causerebbe errore in presenza di più editor
      fateControllerService.registerAction(
        'talmud-bavli',
        {
          command: 'insertHTML',
          name: 'talmud-bavli',
          dropdown: TalmudBavliDropdownComponent
        }
      );
    }
    if (!fateControllerService.getAction('talmud-yerushalmi')) { //controllo per non inizializzare più volte un'azione, causerebbe errore in presenza di più editor
      fateControllerService.registerAction(
        'talmud-yerushalmi',
        {
          command: 'insertHTML',
          name: 'talmud-yerushalmi',
          dropdown: TalmudYerushalmiDropdownComponent
        }
      );
    }
    if (!fateControllerService.getAction('tosefta')) { //controllo per non inizializzare più volte un'azione, causerebbe errore in presenza di più editor
      fateControllerService.registerAction(
        'tosefta',
        {
          command: 'insertHTML',
          name: 'tosefta',
          dropdown: ToseftaDropdownComponent
        }
      );
    }
    if (!fateControllerService.getAction('c-bold')) {
      fateControllerService.registerAction(
        'c-bold',
        {
          command: 'bold',
          name: 'c-bold',
        }
      );
    }
    if (!fateControllerService.getAction('c-italic')) {
      fateControllerService.registerAction(
        'c-italic',
        {
          command: 'italic',
          name: 'c-italic',
        }
      );
    }
    if (!fateControllerService.getAction('c-underline')) {
      fateControllerService.registerAction(
        'c-underline',
        {
          command: 'underline',
          name: 'c-underline',
        }
      );
    }
    if (!fateControllerService.getAction('smallcaps')) {
      fateControllerService.registerAction(
        'smallcaps',
        {
          command: 'insertHTML',
          value: () => {
            return this.handleSmallCaps();
          },
          name: 'smallcaps',
        }
      );
    }
    if (!fateControllerService.getAction('glos-concept')) {
      fateControllerService.registerAction(
        'glos-concept',
        {
          command: 'insertHTML',
          value: () => {
            return this.handleGlossary('lightgreen');
          },
          name: 'glos-concept',
        }
      );
    }
    if (!fateControllerService.getAction('glos-linguistic')) {
      fateControllerService.registerAction(
        'glos-linguistic',
        {
          command: 'insertHTML',
          value: () => {
            return this.handleGlossary('yellow');
          },
          name: 'glos-linguistic',
        }
      );
    }
    if (!fateControllerService.getAction('glos-idiom')) {
      fateControllerService.registerAction(
        'glos-idiom',
        {
          command: 'insertHTML',
          value: () => {
            return this.handleGlossary('orange');
          },
          name: 'glos-idiom',
        }
      );
    }
    if (!fateControllerService.getAction('glos-rabbi')) {
      fateControllerService.registerAction(
        'glos-rabbi',
        {
          command: 'insertHTML',
          value: () => {
            return this.handleGlossary('lightsteelblue');
          },
          name: 'glos-rabbi',
        }
      );
    }
    if (!fateControllerService.getAction('glos-nature')) {
      fateControllerService.registerAction(
        'glos-nature',
        {
          command: 'insertHTML',
          value: () => {
            return this.handleGlossary('lightgrey');
          },
          name: 'glos-nature',
        }
      );
    }
    if (!fateControllerService.getAction('glos-name')) {
      fateControllerService.registerAction(
        'glos-name',
        {
          command: 'insertHTML',
          value: () => {
            return this.handleGlossary('violet');
          },
          name: 'glos-name',
        }
      );
    }
    if (!fateControllerService.getAction('glos-daily')) {
      fateControllerService.registerAction(
        'glos-daily',
        {
          command: 'insertHTML',
          value: () => {
            return this.handleGlossary('red');
          },
          name: 'glos-daily',
        }
      );
    }
    if (!fateControllerService.getAction('view-code')) {
      fateControllerService.registerAction(
        'view-code',
        {
          command: 'copy',
          value: () => {
            this.handleViewCode();
          },
          name: 'view-code',
        }
      );
    }
    if (!fateControllerService.getAction('horizontal-rule')) {
      fateControllerService.registerAction(
        'horizontal-rule',
        {
          command: 'insertHorizontalRule',
          name: 'horizontal-rule',
        }
      );
    }
    if (!fateControllerService.getAction('image')) {
      fateControllerService.registerAction(
        'image',
        {
          command: 'insertImage',
          name: 'image',
          dropdown: FateLinkDropdownComponent,
          detect: ('image' as FateType)
        }
      );
    }
    if (!fateControllerService.getAction('font')) {
      fateControllerService.registerAction(
        'font',
        {
          command: 'fontName',
          name: 'font',
          dropdown: FontComponent,
          detect: ('font' as FateType)
        }
      );
    }
    if (!fateControllerService.getAction('font-style')) {
      fateControllerService.registerAction(
        'font-style',
        {
          command: 'formatBlock',
          name: 'font-style',
          dropdown: FontStyleComponent,
        }
      );
    }
    if (!fateControllerService.getAction('font-size')) {
      fateControllerService.registerAction(
        'font-size',
        {
          command: 'fontSize',
          name: 'font-size',
          dropdown: FontSizeComponent,
          detect: ('font' as FateType)
        }
      );
    }
    if (!fateControllerService.getAction('font-color')) {
      fateControllerService.registerAction(
        'font-color',
        {
          command: 'foreColor',
          name: 'font-color',
          dropdown: FontColorComponent,
          detect: ('font' as FateType)
        }
      );
    }
    if (!fateControllerService.getAction('font-highlight')) {
      fateControllerService.registerAction(
        'font-highlight',
        {
          command: 'hiliteColor',
          name: 'font-highlight',
          dropdown: FontHighlightComponent,
        }
      );
    }

  }

  /**Metodo per la gestione del maiuscoletto */
  private handleSmallCaps(): string {
    let range = window.getSelection();
    let selectionRange = range.getRangeAt(0);
    if (selectionRange.startOffset !== selectionRange.endOffset) { //controllo che vi sia una selezione per evitare rimozioni arbitrarie
      let nuovoHTML = document.createRange().createContextualFragment('<span></span>'); //creo il nuovo document fragment
      nuovoHTML.firstChild.appendChild(selectionRange.extractContents()); //inserisco l'html della selezione
      if (selectionRange.commonAncestorContainer['className'] === 'smallcaps' || selectionRange.commonAncestorContainer.parentElement.className === 'smallcaps') {
        if (selectionRange.commonAncestorContainer.parentElement.className === 'smallcaps') { selectionRange.commonAncestorContainer.parentElement.remove(); }
        return nuovoHTML.firstElementChild.innerHTML ? nuovoHTML.firstElementChild.innerHTML : nuovoHTML.textContent;
      } else {
        return '<span class="smallcaps" style="font-variant: small-caps;">' + nuovoHTML.firstElementChild.innerHTML + '</span>';
      }
    }
    return this.content;
  }

  /**
   * Metodo per la gestione dell'evidenziazione dei glossari
   * @param color {string} il colore di evidenziazione
   */
  private handleGlossary(color: string): string {
    let range = window.getSelection();
    let selectionRange = range.getRangeAt(0);
    if (selectionRange.startOffset !== selectionRange.endOffset) { //controllo che vi sia una selezione per evitare rimozioni arbitrarie
      let nuovoHTML = document.createRange().createContextualFragment('<span></span>'); //creo il nuovo document fragment
      nuovoHTML.firstChild.appendChild(selectionRange.extractContents()); //inserisco l'html della selezione
      if (selectionRange.commonAncestorContainer['className'] === 'glossary' || (selectionRange.commonAncestorContainer.parentElement.className === 'glossary' && selectionRange.commonAncestorContainer.parentElement.style.backgroundColor === color)) {
        if (selectionRange.commonAncestorContainer.parentElement.className === 'glossary') { selectionRange.commonAncestorContainer.parentElement.remove(); }
        return nuovoHTML.firstElementChild.innerHTML ? nuovoHTML.firstElementChild.innerHTML : nuovoHTML.textContent;
      } else {
        return '<span class="glossary" style="background-color: ' + color + ';" rel="?">' + nuovoHTML.firstElementChild.innerHTML + '</span>';
      }
    }
    return this.content;
  }

  /**Metodo per la gestione della visualizzazione del codice sorgente */
  private handleViewCode() {
    if (!this.codeView) {
      this.contentCode = this.cleanResult(document.querySelector('.fate-edit-target').innerHTML);
    } else {
      this.content = this.cleanResult(document.getElementById('editableVariant' + this.editorId).innerText);
    }
    this.codeView = !this.codeView;
  }

  /**
   * Metodo per la pulizie del contenuto dell'editor prima del salvataggio con le opportune sostituzioni di tag
   * @param origin {string} contenuto dell'editor come stringa
   */
  private cleanResult(origin: string): string {
    origin = origin.replace(/<strong>/g, '<span class="bold" style="font-weight: bold;">')
      .replace(/<\/strong>/g, '</span>')
      .replace(/<b>/g, '<span class="bold" style="font-weight: bold;">')
      .replace(/<\/b>/g, '</span>')
      .replace(/<em>/g, '<span class="italic" style="font-style: italic;">')
      .replace(/<\/em>/g, '</span>')
      .replace(/<i>/g, '<span class="italic" style="font-style: italic;">')
      .replace(/<\/i>/g, '</span>')
      .replace(/<u>/g, '<span class="underline" style="font-decoration: underline;">')
      .replace(/<\/u>/g, '</span>')
      .replace(/<span style="background-color: lightgreen;">/g, '<span class="glossary" style="background-color: lightgreen" rel="?">')
      .replace(/<span class="smallcaps" style="font-variant-numeric: normal; font-variant-east-asian: normal;">/g, '<span>');
    return origin;
  }

  /**Metodo per il salvataggio del contenuto dell'editor che viene emesso dall'evento di saving */
  onSubmit() {
    if (this.tipoEditor !== 'note-personali') {
      let cleanResult: string = this.cleanResult(document.querySelector('.fate-edit-target').innerHTML);
      this.saving.emit(cleanResult);
    } else {
      this.saving.emit(this.content);
    }
  }

  /**Metodo per la chiusura dell'editor con emissione dell'evento closing */
  onClose() {
    this.closing.emit();
  }

}